import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "~/common/components/Table/TableElements";
import classes from "~/features/contracts/components/ContractDetails/DeliveriesTable.module.less";
import { DateFormatType, formatDate } from "~/common/utils/DateFormatter";
import { SortDirection, useSorting } from "~/common/hooks/useSorting";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ContractLine } from "~/features/contracts/models/models";
import { Link, LinkType } from "~/common/components/Link";
import { resolveUrl } from "~/utils/url/urlResolver";
import classNames from "classnames";
import Spinner from "~/common/components/Spinner/Spinner";
import { TableContainer } from "~/common/mui";

interface NewContractTableProps {
  contractLines?: ContractLine[];
}

export enum ContractSort {
  disposition = "disposition",
  orderQuantity = "orderQuantity",
  openQuantity = "openQuantity",
  orderNumber = "orderNumber",
}

const SORT_HANDLERS: Dict<(a: ContractLine, b: ContractLine) => number> = {
  [ContractSort.disposition]: (a, b) => {
    const aTime = a.disposition || 0;
    const bTime = b.disposition || 0;
    return aTime === bTime ? 0 : aTime > bTime ? 1 : -1;
  },
  [ContractSort.orderNumber]: (a, b) => parseInt(a.orderNumber) - parseInt(b.orderNumber),
  [ContractSort.openQuantity]: (a, b) => {
    if (a.openQuantity === 0 && b.openQuantity === 0) {
      return (new Date(a.disposition).getTime() || 0) - (new Date(b.disposition).getTime() || 0);
    } else {
      return a.openQuantity === 0 ? -1 : b.openQuantity === 0 ? 1 : 0;
    }
  },
};

const NewDeliveriesTable = ({ contractLines }: NewContractTableProps) => {
  if (!contractLines) {
    return <Spinner />;
  }
  const { t } = useTranslation();
  const [sortCode, setSortCode] = useState(ContractSort.openQuantity);
  const [sortDirection, setSortDirection] = useState(SortDirection.Desc);

  const rows = useSorting(contractLines, SORT_HANDLERS, sortCode, sortDirection);

  const createSortHandler = (codeToSet: ContractSort) => () => {
    if (codeToSet === sortCode) {
      setSortDirection(sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc);
    } else {
      setSortCode(codeToSet);
      setSortDirection(SortDirection.Asc);
    }
  };

  const createTableProps = (boundSortCode: ContractSort) => ({
    onClick: createSortHandler(boundSortCode),
    active: boundSortCode === sortCode,
    direction: boundSortCode === sortCode ? sortDirection : SortDirection.Desc,
  });

  const contractEntries = rows?.map((contract) => {
    const disposition = formatDate(contract.disposition, DateFormatType.DATE);
    const orderDetailUrl = resolveUrl(`/my-account/order?id=${contract.orderNumber}`);
    const noQuantity = contract.openQuantity === 0;
    return (
      <TableRow>
        <TableCell className={classNames(classes.cell, noQuantity && classes.pastDelivery)}>
          <p>{disposition ?? "-"}</p>
        </TableCell>
        <TableCell className={classNames(classes.cell, noQuantity && classes.pastDelivery)}>
          <p>{contract.orderQuantity ?? "-"}</p>
        </TableCell>
        <TableCell className={classNames(classes.cell, noQuantity && classes.pastDelivery)}>
          <p>{contract.openQuantity ?? "-"}</p>
        </TableCell>
        <TableCell align="right" className={classes.cell}>
          <Link variant={LinkType.NAVIGATION} className={classes.orderNumber} href={orderDetailUrl}>
            <p>{contract.orderNumber}</p>
          </Link>
        </TableCell>
      </TableRow>
    );
  });
  return (
    <TableContainer className={classes.tableContainer}>
      <Table stickyHeader className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerLabel}>
              <TableSortLabel {...createTableProps(ContractSort.disposition)}>
                {t("contractDetails.table.disposition")}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.headerLabel}>{t("contractDetails.table.orderQuantity")}</TableCell>
            <TableCell className={classes.headerLabel}>{t("contractDetails.openQuantity")}</TableCell>
            <TableCell className={classNames(classes.headerLabel, classes.alignRight)} align={"right"}>
              <TableSortLabel {...createTableProps(ContractSort.orderNumber)}>
                {t("contractDetails.table.orderNumber")}
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{contractEntries}</TableBody>
      </Table>
    </TableContainer>
  );
};

export default NewDeliveriesTable;
